import React, { useState, useEffect, useRef } from "react"
import PropTypes from "prop-types"
import BreadcrumbWrapper from "../BreadcrumbWrapper/BreadcrumbWrapper"
import {
  SEARCH_FIELD,
  SeachNotFound,
  labels,
} from "../../constants/label.constants"
import Button from "../UI/Button/Button"
import Typography from "../UI/Typography/Typography"
import Link from "../UI/Link/Link"
import PaginationNews from "../PaginationNews/PaginationNews"
const { getGlobalSearchResults } = require("../../adapters/algolia-adapter")

export const getUrlValues = () => {
  if (typeof window !== "undefined") {
    const queryParameters = new URLSearchParams(window?.location?.search)
    return {
      searchText: queryParameters.get("term"),
      ...(queryParameters.get("page")
        ? { pageNo: queryParameters.get("page") }
        : {}),
    }
  }
}

const GlobalSearchListing = props => {
  const { heading } = props
  const [searchTerm, setSearchTerm] = useState("")
  const [searchResult, setSearchResult] = useState({})
  const [noSearchLabel, setNoSearchLabel] = useState("")
  const [globalSearchObj, setGlobalSearchObj] = useState({
    pageNo: 1,
    search: "",
  })
  const { hits = [], nbHits, nbPages, hitsPerPage } = searchResult
  const firstUpdate = useRef(true)

  const updatePageUrl = ({ search, pageNo = 0 }) => {
    if (typeof window !== "undefined") {
      // page number query
      const pathName = window?.location?.pathname
      // create query params
      let queryParams = ""
      queryParams += `?term=${search}`
      queryParams += pageNo ? `&page=${pageNo}` : ""
      const newUrl = window.location.origin + pathName + queryParams
      window?.history?.pushState(null, null, newUrl)
    }
  }

  const fetchSearchResults = async ({ search, pageNo }) => {
    const searchKey = search?.trim()
    if (searchKey) {
      const results = await getGlobalSearchResults({ search, pageNo })
      setNoSearchLabel("")
      setSearchResult(results)
    } else {
      setNoSearchLabel(labels?.noSearchValue)
      setSearchResult({})
    }
  }

  const onPageChange = pageNumber => {
    setGlobalSearchObj({ ...globalSearchObj, pageNo: pageNumber })
  }

  useEffect(() => {
    const values = getUrlValues()
    const { searchText, pageNo } = values

    const obj = { search: searchText, pageNo: pageNo && parseInt(pageNo) }
    setSearchTerm(searchText)
    setGlobalSearchObj({ ...obj })

    firstUpdate.current = false
  }, [])

  useEffect(() => {
    if (!firstUpdate.current) {
      ;(async () => {
        if (typeof window != "undefined") {
          window?.scrollTo({
            top: 0,
            left: 0,
            behavior: "instant",
          })
        }

        updatePageUrl({ ...globalSearchObj })
        await fetchSearchResults({ ...globalSearchObj })
      })()
    }
  }, [globalSearchObj])

  const renderNoRecordFound = text => {
    return (
      <div className="globalSearchNoDataRow">
        <span>{text}</span>
      </div>
    )
  }

  // format search result text
  const formatData = (content = "", contentType) => {
    let text = content
    if (contentType === "description" && content.length > 199) {
      text = content.substring(0, 199) + " ..."
    }
    const termsArray =
      globalSearchObj.search
        ?.toLowerCase()
        ?.split(" ")
        .filter(term => !labels?.excludedTerms.includes(term)) || []
    const newText = text
      .split(" ")
      .map((ele, idx) => {
        if (termsArray.includes(ele.toLowerCase())) {
          return `<span key={${ele}-${idx}} className="bg-yellow text-pureRed mr-[0.35em] inline-block last:mr-0 leading-[1]">${ele}</span>`
        } else {
          return ele
        }
      })
      .join(" ")
    return newText
  }

  const renderSearchResult = (article, index) => {
    const { path, title, content, description1 } = article
    const longText = content || description1

    return (
      <div key={`tag-${index}`} className="globalSearchDescriptionRow">
        <Link url={path} className="globalSearchDescriptionLink">
          <div>{title && <Typography content={formatData(title)} />}</div>
        </Link>
        <div className="globalSearchTypoDescription">
          {longText && (
            <Typography content={formatData(longText, "description")} />
          )}
        </div>
      </div>
    )
  }

  const breadCrumbData = {
    heading: heading,
    isBreadcrumb: true,
  }

  const handleSearch = e => {
    setSearchTerm(e.target.value)
  }

  const handleSearchSubmit = () => {
    setGlobalSearchObj({ search: searchTerm })
  }

  const onKeyDown = e => {
    if (e.key === "Enter" && searchTerm) {
      handleSearchSubmit()
    }
  }

  return (
    <div>
      <div className="globalSearchBreadcrumbWrapper">
        <div className="globalSearchBreadcrumb">
          <BreadcrumbWrapper {...breadCrumbData} />
        </div>
        <div className="searchBarContainer">
          <div className="form flex items-baseline justify-center w-full pb-[25px]">
            <input
              type="text"
              aria-label={SEARCH_FIELD.ariaLabel2}
              value={searchTerm}
              onChange={handleSearch}
              onKeyDown={onKeyDown}
              className="input font-Light bg-none border-b-[1px] border-solid border-[#666666] pl-[15px] text-16 mr-10 lg:text-[48px] text-black inline-block w-full font-[300]"
            />
            <Button
              type="button"
              ariaLabel={SEARCH_FIELD.ariaLabel3}
              onClick={handleSearchSubmit}
              className="image flex justify-center items-center bg-[#4f2170] text-white w-[30px] md:w-[50px] h-[30px] md:h-[50px] cursor-pointer icon-search text-[21px] lg:text-[30px] rounded-[2px]"
            ></Button>
          </div>
        </div>
      </div>
      <div className="globalSearchResultsWrapper">
        {noSearchLabel
          ? renderNoRecordFound(noSearchLabel)
          : !hits?.length &&
            renderNoRecordFound(`${SeachNotFound} ${globalSearchObj.search}.`)}
        {!!hits?.length && (
          <div className="searchResultsWrapper">
            {hits?.length > 0 && (
              <div className="resulstFound">
                <span>
                  {nbHits} result(s) found for search term{" "}
                  {globalSearchObj.search}.
                </span>
              </div>
            )}
            {hits?.length > 0 &&
              hits?.map((article, idx) => renderSearchResult(article, idx))}
            {hits?.length > 0 && (
              <PaginationNews
                currentPage={globalSearchObj.pageNo}
                totalPages={nbPages}
                pageSize={hitsPerPage}
                onPageChange={onPageChange}
                totalCount={nbHits}
                hideAtFirst={true}
              />
            )}
          </div>
        )}
      </div>
    </div>
  )
}

GlobalSearchListing.propTypes = {
  heading: PropTypes.string,
}

export default GlobalSearchListing
